import { usePatientContext } from '@/components/Contexts/PatientContext';

import SvgChart, { SVGRenderer } from '@wuba/react-native-echarts/svgChart';
import * as echarts from 'echarts/core';
import { useEffect, useRef, useState } from 'react';
import { PieChart } from 'echarts/charts';
import { GridComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import DateUtils from '@/utils/DateUtils';
import { LayoutChangeEvent, View } from 'react-native';
import { useTranslation } from 'react-i18next';

// Register extensions
echarts.use([TitleComponent, TooltipComponent, GridComponent, SVGRenderer, PieChart]);

const E_HEIGHT = 250;
const E_WIDTH = 300;

export const getHabitudeChartOptions = (
  patient,
  t,
  minPercent = '40%',
  maxPercent = '60%',
  fontWeight = 'bold',
  fontSize = '14'
) => {
  const habitudes = patient?.habitudes[0];

  const minCoucher = DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.heureCoucher));
  const minReveil = DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.heureReveil));
  const totalTime =
    minCoucher -
    minReveil -
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsSieste)) +
    (minCoucher < minReveil ? 1440 : 0);

  const near =
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsLecture)) +
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsTablette)) +
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsSmartphone)) +
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsAutreVisionPres));

  const intermediate =
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsDiscussion)) +
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsJeuxSociete)) +
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsOrdinateur)) +
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsCuisine));

  const distance =
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsConduite)) +
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsTelevision));

  console.log(totalTime, near, intermediate, distance);

  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{b} {d}%',
    },
    color: ['#4c99c1', '#86abda', '#b6bdea'],
    grid: {
      left: '5%',
      right: '5%',
      bottom: '5%',
      top: '5%',
    },
    series: [
      {
        center: ['50%', '40%'],
        type: 'pie',
        id: 'vision',
        radius: [minPercent, maxPercent],
        data: [
          {
            name: 'VL',
            value: distance / totalTime,
          },
          {
            name: 'VI',
            value: intermediate / totalTime,
          },
          {
            name: 'VP',
            value: near / totalTime,
          },
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        label: {
          show: true,
          formatter(param: { name: string; percent: number }) {
            let name = t(param.name);
            // correct the percentage
            return name + '\n(' + Math.round(param.percent) + '%)';
          },
          position: 'outside',
          fontSize,
          fontWeight,
          overflow: 'breakAll',
          width: 80,
        },
        labelLine: {
          show: true,
          length: 8,
          length2: 6,
        },
      },
    ],
    legend: {
      show: true,
      bottom: '3%',
      formatter: function (name: string) {
        let newName = '';
        switch (name) {
          case 'VL':
            newName = t('Vision de loin');
            break;
          case 'VI':
            newName = t('Vision intermédiaire');
            break;
          case 'VP':
            newName = t('Vision de près');
            break;
        }
        return t(name) + ' : ' + newName;
      },
    },
  };
  return option;
};

export const getHabitudesChart = (
  patient,
  t,
  width,
  height,
  component,
  minPercent = '40%',
  maxPercent = '60%',
  fontWeight = 'bold',
  fontSize = '14'
) => {
  const option = getHabitudeChartOptions(patient, t, minPercent, maxPercent, fontWeight, fontSize);

  // @ts-ignore
  const chart = echarts.init(component, 'light', {
    renderer: 'svg',
    width: width,
    height: height,
    ssr: component ? false : true,
  });
  chart.setOption(option);
  return chart;
};

const HabitudesChart = ({ language }: { language: string }) => {
  const { patient } = usePatientContext();

  const chartRef = useRef<any>(null);
  const svgRef = useRef<any>(null);
  const [width, setWidth] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.resize({
        width: width,
        height: Math.max(E_HEIGHT, width * 0.25),
      });
    }
  }, [width]);

  useEffect(() => {
    let chart: any;
    if (svgRef.current && patient) {
      chart = getHabitudesChart(
        patient,
        t,
        width,
        Math.max(E_HEIGHT, width * 0.25),
        svgRef.current
      );

      chartRef.current = chart;
    }
    return () => chart?.dispose();
  }, [patient, language]);

  const handleLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setWidth(width);
  };

  return (
    <View onLayout={handleLayout} style={{ flex: 1, height: 250 }}>
      <SvgChart ref={svgRef} />
    </View>
  );
};

export default HabitudesChart;
